/* eslint-disable no-undef */
import { ref, onBeforeMount, onMounted, computed } from 'vue';
import { useCreateSharedComposable } from './useCreateSharedComposable';
import { useStore } from './store';

export function featurebase() {
  const isFeaturebaseLoading = ref(false);
  const unreadChangelogCount = ref(0);
  const isInitialized = ref(false);

  const store = useStore();
  const currentUser = computed(() => store.getters.getCurrentUser);

  // TODO: Future implementation
  const isChangelogOpen = ref(false);

  function setUnreadChangelogCount(count) {
    unreadChangelogCount.value = count;
  }

  // Load SDK script first
  onBeforeMount(() => {
    if (document.getElementById('featurebase-sdk')) {
      return; // Already loaded
    }

    const script = document.createElement('script');
    script.src = 'https://do.featurebase.app/js/sdk.js';
    script.id = 'featurebase-sdk';
    document.head.appendChild(script);
  });

  // Initialize widgets after mount
  onMounted(() => {
    const currentUserName = currentUser.value?.name || '';

    const initializeWidgets = () => {
      const win = window;
      if (typeof win.Featurebase !== 'function') {
        // eslint-disable-next-line func-names
        win.Featurebase = function () {
          (win.Featurebase.q = win.Featurebase.q || []).push(arguments);
        };
      }

      // Initialize portal widget
      win.Featurebase('initialize_portal_widget', {
        organization: 'commslayer',
        placement: 'left',
        fullScreen: false,
        initialPage: 'MainView',
        locale: 'en',
        metadata: null,
      });

      // Initialize changelog widget
      win.Featurebase(
        'init_changelog_widget',
        {
          organization: 'commslayer',
          dropdown: {
            enabled: true, // Enable dropdown view
            placement: 'top', // Position dropdown to the right
          },
          popup: {
            enabled: true, // Disable popup view
            usersName: currentUserName, // This will show the user's name in the popup as a greeting
            autoOpenForNewUpdates: true, // This will open the popup for new updates
          },
          theme: 'light',
          locale: 'en',
        },
        (error, data) => {
          if (data?.action === 'unreadChangelogsCountChanged') {
            setUnreadChangelogCount(data.unreadCount || 0);
            // eslint-disable-next-line no-console
            console.info('featurebase', data);
          }
        }
      );

      isInitialized.value = true;
    };

    // Check if script is already loaded, otherwise wait for it
    if (window.Featurebase) {
      initializeWidgets();
    } else {
      // Wait for script to load
      const checkScript = setInterval(() => {
        if (window.Featurebase) {
          clearInterval(checkScript);
          initializeWidgets();
        }
      }, 100);

      // Fallback timeout
      setTimeout(() => {
        clearInterval(checkScript);
        if (!isInitialized.value) {
          initializeWidgets();
        }
      }, 5000);
    }
  });

  const loadFeaturebaseIfNeeded = () => {
    return new Promise(resolve => {
      if (isInitialized.value && typeof window.Featurebase === 'function') {
        resolve();
        return;
      }

      // Wait for initialization
      const checkInit = setInterval(() => {
        if (isInitialized.value && typeof window.Featurebase === 'function') {
          clearInterval(checkInit);
          resolve();
        }
      }, 100);

      // Fallback timeout
      setTimeout(() => {
        clearInterval(checkInit);
        resolve();
      }, 3000);
    });
  };

  const featurebaseAction = action => {
    if (action === 'open') {
      return loadFeaturebaseIfNeeded().then(() => {
        if (typeof window.Featurebase === 'function') {
          window.Featurebase('open_widget');
        }
      });
    }
    if (typeof window.Featurebase === 'function') {
      window.Featurebase(`${action}_widget`);
    }
    return Promise.resolve();
  };

  const openFeaturebaseWidget = async () => {
    isFeaturebaseLoading.value = true;

    try {
      await featurebaseAction('open');
    } finally {
      isFeaturebaseLoading.value = false;
    }
  };

  const closeFeaturebase = () => {
    featurebaseAction('close');
  };

  // Not needed for dropdown view - widget handles clicks via data-featurebase-changelog attribute
  const openFeaturebaseChangelog = async () => {
    // Dropdown is triggered automatically by FeatureBase when clicking elements with data-featurebase-changelog
    // eslint-disable-next-line no-console
    console.warn(
      'Dropdown view is triggered automatically via data-featurebase-changelog attribute'
    );
  };

  const initializeFeaturebase = async () => {
    await loadFeaturebaseIfNeeded();
  };

  const handleOpenFeaturebase = () => {
    openFeaturebaseWidget();
  };

  return {
    isFeaturebaseLoading,
    unreadChangelogCount,
    isInitialized,
    isChangelogOpen,
    initializeFeaturebase,
    openFeaturebaseWidget,
    closeFeaturebase,
    openFeaturebaseChangelog,
    featurebaseAction,
    handleOpenFeaturebase,
  };
}

export const useFeaturebase = useCreateSharedComposable(featurebase);
